import {
  Clock,
  TrendingUp,
  DollarSign,
  Users,
  Eye,
  Shield,
} from "lucide-react";

const benefits = [
  {
    icon: Clock,
    title: "Ahorra 40% de Tiempo",
    description: "Automatiza tareas administrativas y reduce el papeleo. Más tiempo para crecer tu negocio.",
  },
  {
    icon: TrendingUp,
    title: "Reduce Errores 90%",
    description: "Elimina errores de transcripción manual. Datos precisos desde el campo hasta la oficina.",
  },
  {
    icon: DollarSign,
    title: "Mejora Facturación",
    description: "No pierdas trabajos sin facturar. Genera documentos automáticamente desde el móvil.",
  },
  {
    icon: Users,
    title: "Aumenta Productividad",
    description: "Técnicos más eficientes con toda la información al alcance. Menos viajes innecesarios.",
  },
  {
    icon: Eye,
    title: "Visibilidad Total",
    description: "Sabe qué está pasando en cada momento. Toma decisiones con datos en tiempo real.",
  },
  {
    icon: Shield,
    title: "Datos Seguros",
    description: "Cifrado end-to-end y backups automáticos. Cumplimiento GDPR garantizado.",
  },
];

export function LandingBenefits() {
  return (
    <section id="benefits" className="py-20 px-4 sm:px-6 lg:px-8 relative overflow-hidden bg-gradient-to-b from-background to-muted/20">
      {/* Background decoration */}
      <div className="absolute top-0 left-0 w-96 h-96 rounded-full bg-raven-electric/10 blur-3xl" />
      
      <div className="container mx-auto max-w-7xl relative z-10">
        <div className="text-center mb-16 animate-fade-in">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
            Beneficios que <span className="landing-gradient-text">Transforman</span>
          </h2>
          <p className="text-lg text-muted-foreground">
            Impacto real y medible en tu negocio desde el día uno
          </p>
        </div>

        {/* Benefits Grid - Bento style */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
          {benefits.map((benefit, index) => (
            <div
              key={index}
              className="group landing-glass-card p-8"
              style={{
                animation: 'slide-up 0.6s ease-out backwards',
                animationDelay: `${index * 0.1}s`
              }}
            >
              {/* Gradient overlay on hover */}
              <div className="absolute inset-0 bg-gradient-to-br from-raven-electric/0 to-raven-violet/0 group-hover:from-raven-electric/10 group-hover:to-raven-violet/10 transition-all duration-500 rounded-2xl" />
              
              {/* Icon */}
              <div className="relative mb-6 w-16 h-16 rounded-2xl bg-gradient-to-br from-raven-electric to-raven-violet flex items-center justify-center group-hover:scale-110 group-hover:rotate-6 transition-transform duration-300 shadow-lg shadow-raven-electric/30">
                <benefit.icon className="w-8 h-8 text-white" />
              </div>
              
              {/* Content */}
              <div className="relative">
                <h3 className="font-bold text-xl mb-3 text-foreground group-hover:landing-gradient-text transition-all">
                  {benefit.title}
                </h3>
                <p className="text-muted-foreground leading-relaxed">
                  {benefit.description}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Implementation Callout */}
        <div 
          className="landing-glass-card p-8 max-w-4xl mx-auto my-16 animate-fade-in relative overflow-hidden group"
          style={{animationDelay: '0.6s'}}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-raven-electric/5 to-raven-violet/5 group-hover:from-raven-electric/10 group-hover:to-raven-violet/10 transition-all duration-500" />
          
          <div className="relative flex items-start gap-6">
            <div className="flex-shrink-0">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-raven-electric to-raven-violet flex items-center justify-center shadow-lg shadow-raven-electric/30 group-hover:scale-110 transition-transform">
                <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
            </div>
            <div className="flex-1">
              <h3 className="text-2xl font-bold mb-3 landing-gradient-text">
                Sin Barreras de Entrada
              </h3>
              <p className="text-muted-foreground leading-relaxed">
                A diferencia de otros sistemas empresariales, con RAVEN no hay períodos de implementación. Creas tu cuenta y estás listo para trabajar. Si necesitas migrar datos históricos, te acompañamos en el proceso - pero puedes empezar a usar RAVEN inmediatamente.
              </p>
            </div>
          </div>
        </div>

        {/* Stats Section */}
        <div className="grid md:grid-cols-3 gap-8 mt-20">
          {[
            { value: "50+", label: "Empresas del Sector", suffix: "" },
            { value: "15k+", label: "Instalaciones Gestionadas", suffix: "" },
            { value: "100%", label: "Trabajo Offline", suffix: "" }
          ].map((stat, index) => (
            <div 
              key={index}
              className="text-center group cursor-default"
              style={{
                animation: 'scale-in 0.4s ease-out backwards',
                animationDelay: `${index * 0.1 + 0.5}s`
              }}
            >
              <div className="text-5xl lg:text-6xl font-bold landing-gradient-text mb-2 group-hover:scale-110 transition-transform">
                {stat.value}
              </div>
              <p className="text-muted-foreground font-medium">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Testimonial from sector entrepreneur */}
        <div className="mt-16 landing-glass-card p-8 max-w-4xl mx-auto animate-fade-in" style={{animationDelay: '0.8s'}}>
          <div className="flex items-start gap-6">
            <div className="flex-shrink-0">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-raven-electric to-raven-violet flex items-center justify-center">
                <Shield className="w-8 h-8 text-white" />
              </div>
            </div>
            <div className="flex-1">
              <p className="text-lg text-foreground leading-relaxed mb-4 italic">
                "Como empresario del sector de seguridad, participé activamente en el desarrollo de RAVEN desde sus inicios. Cada funcionalidad refleja necesidades reales que enfrentamos día a día. Es la primera herramienta que realmente entiende cómo trabajamos."
              </p>
              <p className="text-sm font-semibold text-raven-electric">
                — Fundador, Empresa de Seguridad y Contraincendios
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
